import { useContext, useEffect, useRef } from "react";
import isEmpty from "lodash/isEmpty";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";

import { DISPENSARY_MEDICAL_DISCLAIMER } from "constants/disclaimers";
import MenuContext from "context/MenuContext";
import { MenuItem } from "custom-types/MenuItem";
import {
  getCurrentDispensary,
  getIsCanadianDispensary,
  getIsDualMenu,
} from "redux/selectors/dispensary";
import { getUserPrefersMedical } from "redux/selectors/user";
import { MENU_TITLES } from "utils/menuTypeUtils";
import parseNextRouterAsPath from "utils/parseNextRouterAsPath";

import ActiveDealsCarousel from "components/Dispensary/ActiveDealsCarousel";
import CategoryCarousel from "components/Dispensary/CategoryCarousel";
import MedicalInfo from "components/Dispensary/MedicalInfo";
import PromotedBrandCarousel from "components/Dispensary/PromotedBrandCarousel";
import SearchBar from "components/Dispensary/SearchBar";
import MedicalDisclaimers from "components/MedicalDisclaimers";
import MenuFilters from "components/MenuFilters";
import useMenuFilterSearch from "components/MenuFilters/hooks/useMenuFilterSearch";
import SearchAd from "components/SearchAd";

import { DealsBanner } from "../DealsBanner/DealsBanner";
import DispensaryMenuItems from "../DispensaryMenuItems";
import LastUpdatedTime from "../LastUpdatedTime";
import MenuSortDropDown from "../MenuSortDropDown";
import Pagination from "../Pagination/Pagination";
import StaffPicksCarousel from "../StaffPicksCarousel";
import StrainEffectCopy from "./StrainEffectCopy";

const MenuContainer: React.FC = () => {
  const {
    query: { slug, menu_type },
    asPath,
  } = useRouter();
  const { query: parsedQuery } = parseNextRouterAsPath(asPath);

  const { selectors } = useContext(MenuContext);

  const {
    searchMenuItems,
    results: { menuItems, availableFilters, availableSorts, totalItems },
    totalPages,
    loading,
  } = useMenuFilterSearch(selectors);

  const isDualMenuDispensary = useSelector(getIsDualMenu);
  const isCanadianDispensary = useSelector(getIsCanadianDispensary);
  const prefersMedical = useSelector(getUserPrefersMedical);
  const currentDispensary = useSelector(getCurrentDispensary);

  const hideCategoryCoins = currentDispensary.state === "AB";

  const userMedicalPreference = prefersMedical
    ? MENU_TITLES.med.shortTitle
    : MENU_TITLES.rec.shortTitle;

  const currentMenuType =
    (menu_type && String(menu_type)) || userMedicalPreference;

  const isMounted = useRef(false);
  useEffect(() => {
    // This check is here to prevent a redundant call to fetch data on initial mount.
    // We only want to fetch new data when dependencies change.
    if (isMounted.current) {
      searchMenuItems(slug as string, {
        ...parsedQuery,
        ...(isDualMenuDispensary ? { menu_type: currentMenuType } : {}),
      });
    }

    isMounted.current = true;
  }, [slug, JSON.stringify(parsedQuery)]);

  return (
    <div className="pb-section">
      <div className="container pt-lg">
        <MedicalInfo />
        <SearchBar className="mt-xl mb-lg" />
      </div>
      <div className="mt-md">
        <LastUpdatedTime
          overrideTotalItems={totalItems}
          className="pb-2 container"
        />
        <MenuFilters
          availableFilters={availableFilters}
          loading={loading}
          mobileClassName="mt-md"
        >
          {isCanadianDispensary && (
            <SearchAd
              className="my-xl"
              page="store"
              tag="menu_upper"
              customTargeting={{ dispensary: slug as string }}
            />
          )}
          <MenuSortDropDown
            totalItems={totalItems}
            availableSorts={availableSorts}
          />
          {!hideCategoryCoins && isEmpty(parsedQuery) && (
            <CategoryCarousel
              showNewCoinListExperience={true}
              filterable={true}
              parsedQuery={`${parsedQuery}`}
              className="pb-xxl"
              tab="menu"
            />
          )}
          <DealsBanner />
          {isEmpty(parsedQuery) && (
            <div data-testid="merchandising-tray">
              <ActiveDealsCarousel cardsPerSlide={3} className="mb-section" />
              <PromotedBrandCarousel cardsPerSlide={3} className="mb-section" />
              <StaffPicksCarousel
                cardsPerSlide={3}
                className="mb-section"
                filterable
                maxCardCount={6}
              />
            </div>
          )}
          <StrainEffectCopy />
          <DispensaryMenuItems
            hasActiveFilter={!isEmpty(parsedQuery)}
            menuItems={menuItems as MenuItem[]}
            loading={loading}
          />
          {menuItems.length ? <Pagination totalPages={totalPages} /> : null}
        </MenuFilters>
        <MedicalDisclaimers
          usDisclaimer={DISPENSARY_MEDICAL_DISCLAIMER}
          className="container mt-xxl"
        />
      </div>
    </div>
  );
};

export default MenuContainer;
